"use client";

import type React from "react";
import { createContext, useState, useEffect } from "react";

type Theme = "light" | "dark";

type ThemeContextType = {
	theme: Theme;
	toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Export for use in hook file only
export { ThemeContext };

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [theme, setTheme] = useState<Theme>("dark");
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		// This code will only run on the client side
		const savedTheme = localStorage.getItem("theme") as Theme | null;
		const initialTheme = savedTheme || "dark"; // Default to dark theme

		setTheme(initialTheme);
		setIsInitialized(true);
	}, []);

	useEffect(() => {
		if (isInitialized) {
			localStorage.setItem("theme", theme);
			if (theme === "dark") {
				document.documentElement.classList.add("dark");
				// Update favicon for dark mode
				const link = document.querySelector(
					"link[rel~='icon']",
				) as HTMLLinkElement;
				if (link) link.href = "/icon-dark.svg";
			} else {
				document.documentElement.classList.remove("dark");
				// Update favicon for light mode
				const link = document.querySelector(
					"link[rel~='icon']",
				) as HTMLLinkElement;
				if (link) link.href = "/icon-light.svg";
			}
		}
	}, [theme, isInitialized]);

	const toggleTheme = () => {
		setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
	};

	return (
		<ThemeContext.Provider value={{ theme, toggleTheme }}>
			{children}
		</ThemeContext.Provider>
	);
};
